import React, { useMemo, useState } from 'react';
import { Box, Stack, Typography, Paper, Button, Alert, Divider } from '@mui/material';
import { Edit, WarningAmber } from '@mui/icons-material';

import { AdmissionCycle, AdmissionApplication, RubricScore } from '../../../types/admissions';
import TestScoresDialog from '../dialogs/TestScoresDialog';
import InterviewRoundDialog, { RECOMMENDATION_LABEL } from '../dialogs/InterviewRoundDialog';
import CustomScoresDialog from '../dialogs/CustomScoresDialog';
import { ToneChip } from '../../common/StatusChip';
import { GroupLabel } from '../tabLayout';
import {
  assessmentGaps,
  testOutcome,
  markingBlockers,
  planFor,
  roundsLabel,
} from '../../../utils/admissionAssessment';
import dateTimeUtils from '../../../utils/dateTimeUtils';
import { useModulePermissions } from '../../../hooks/useModulePermissions';

/**
 * Assessment tab — marks for the stages this cycle runs, read from the
 * cycle's Selection-process settings (a switched-off stage simply isn't
 * here). Saving the last test or interview mark moves the applicant to
 * Assessed; merit re-ranks on every save.
 */

interface Props {
  app: AdmissionApplication;
  cycle: AdmissionCycle;
  onChanged: () => Promise<void> | void;
}

const num = { fontVariantNumeric: 'tabular-nums' } as const;


/** A round's unscored answers — "Attended by: Both parents" — under its score. */
const RoundAnswers: React.FC<{ scores?: RubricScore[] }> = ({ scores }) => {
  const answered = (scores || []).filter(r => r.kind && r.kind !== 'marks' && r.answer);
  if (!answered.length) return null;
  return (
    <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: 'minmax(0, auto) 1fr', columnGap: 1.5, rowGap: 0.25 }}>
      {answered.map(r => (
        <React.Fragment key={r.criterion}>
          <Typography variant="caption" color="text.secondary">{r.criterion}</Typography>
          <Typography variant="caption" fontWeight={500} sx={{ whiteSpace: 'pre-wrap' }}>{r.answer}</Typography>
        </React.Fragment>
      ))}
    </Box>
  );
};

const CandidateAssessmentTab: React.FC<Props> = ({ app, cycle, onChanged }) => {
  const { canWrite } = useModulePermissions();
  const canManage = canWrite('admissions');
  const plan = useMemo(() => planFor(cycle, app), [cycle, app]);
  const [dialog, setDialog] = useState<null | { kind: 'test' } | { kind: 'custom' } | { kind: 'round'; key: string }>(null);

  const outcome = testOutcome(app, plan);
  const gaps = assessmentGaps(app, plan);
  const failFlags = (app.eligibilityFlags || []).filter(f => f.severity === 'fail');
  const waiver = failFlags.length > 0 ? app.eligibilityWaiver : null;
  const warnFlags = (app.eligibilityFlags || []).filter(f => f.severity === 'warn');

  // Why marks can't be entered right now, if they can't.
  const blockers = markingBlockers(app, plan);
  const lockReason = blockers.custom;
  const markingReason = blockers.interview;
  const testReason = blockers.test;

  const done = async () => { setDialog(null); await onChanged(); };
  const scoredRound = (key: string) => (app.interviewRounds || []).find(r => r.roundKey === key);

  return (
    <>
      <Stack spacing={3}>
        {/* ── Standing ───────────────────────────────────────── */}
        <Paper variant="outlined" sx={{ borderRadius: 2.5, px: 2.5, py: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: plan.test ? '1fr 1fr' : '1fr' }, gap: 2.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Merit</Typography>
              {app.meritRank != null ? (
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <Typography variant="h5" fontWeight={700} sx={num}>#{app.meritRank}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={num}>score {app.computedMeritScore?.toFixed(2)}</Typography>
                </Stack>
              ) : (
                <>
                  <Typography variant="subtitle1" fontWeight={600}>Not ranked yet</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {app.meritGaps?.length ? `Missing: ${app.meritGaps.join(', ')}` : 'Ranked once the assessment is complete.'}
                  </Typography>
                </>
              )}
            </Box>
            {plan.test && (
              <Box>
                <Typography variant="caption" color="text.secondary">Entrance test</Typography>
                {outcome ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h5" fontWeight={700} sx={num}>{outcome.obtained}<Typography component="span" color="text.secondary"> / {outcome.max}</Typography></Typography>
                    <ToneChip label={outcome.passed ? 'Passed' : 'Below pass mark'} tone={outcome.passed ? 'success' : 'error'} />
                  </Stack>
                ) : (
                  <Typography variant="subtitle1" fontWeight={600}>Not marked yet</Typography>
                )}
                <Typography variant="caption" color="text.secondary">Pass mark {plan.test.passingMarks} of {plan.test.maxMarks}</Typography>
              </Box>
            )}
          </Box>
          {app.status === 'AssessmentScheduled' && gaps.length > 0 && (
            <>
              <Divider sx={{ my: 1.75 }} />
              <Typography variant="body2" color="text.secondary">
                Moves to <b>Assessed</b> once every mark is in. Still to mark: {gaps.join(', ')}.
              </Typography>
            </>
          )}
        </Paper>

        {(failFlags.length > 0 || warnFlags.length > 0) && (
          <Alert severity={waiver ? 'info' : failFlags.length ? 'error' : 'warning'} variant="outlined" icon={<WarningAmber fontSize="small" />} sx={{ borderRadius: 2.5 }}>
            <Typography variant="body2" fontWeight={600}>
              {waiver ? 'Eligibility rules waived' : failFlags.length ? 'Does not meet the eligibility rules' : 'Eligibility needs a look'}
            </Typography>
            {waiver && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {waiver.reason} · {dateTimeUtils.formatDateForDisplay(waiver.at)}
              </Typography>
            )}
            {[...failFlags, ...warnFlags].map((f, i) => (
              <Typography key={i} variant="body2" color="text.secondary">{f.message}</Typography>
            ))}
          </Alert>
        )}

        {/* ── Entrance test ──────────────────────────────────── */}
        {plan.test && (
          <Box>
            <GroupLabel action={!testReason && canManage && (
              <Button size="small" startIcon={<Edit fontSize="small" />} onClick={() => setDialog({ kind: 'test' })} sx={{ textTransform: 'none' }}>
                {app.testScores?.length ? 'Edit marks' : 'Enter marks'}
              </Button>
            )}>
              Entrance test{plan.admitCards && app.admitCard?.rollNumber ? ` · Roll ${app.admitCard.rollNumber}` : ''}
            </GroupLabel>
            <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
              {plan.test.papers.map((p, i) => {
                const s = (app.testScores || []).find(x => x.paperKey === p.key);
                return (
                  <Stack key={p.key} direction="row" alignItems="center" sx={{ px: 2, py: 1.25, borderTop: i ? 1 : 0, borderColor: 'divider' }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>{p.subject}</Typography>
                    {!s ? <Typography variant="body2" color="text.disabled">Not marked</Typography>
                      : s.absent ? <ToneChip label="Absent" tone="warning" />
                        : <Typography variant="body2" fontWeight={600} sx={num}>{s.obtained} <Typography component="span" variant="body2" color="text.secondary">/ {p.maxMarks}</Typography></Typography>}
                  </Stack>
                );
              })}
            </Paper>
            {testReason && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>{testReason}</Typography>}
          </Box>
        )}

        {/* ── Interview ──────────────────────────────────────── */}
        {plan.interview && (
          <Box>
            <GroupLabel>{roundsLabel(plan.interview.rounds)}</GroupLabel>
            <Stack spacing={1.25}>
              {plan.interview.rounds.map(r => {
                const s = scoredRound(r.key);
                return (
                  <Paper key={r.key} variant="outlined" sx={{ borderRadius: 2.5, px: 2, py: 1.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {s ? [s.recommendation && RECOMMENDATION_LABEL[s.recommendation], s.interviewer].filter(Boolean).join(' · ') || 'Marked'
                            : r.rubric?.length ? `${r.rubric.length} question${r.rubric.length === 1 ? '' : 's'}` : 'Single mark'}
                        </Typography>
                      </Box>
                      {s
                        ? <Typography variant="body2" fontWeight={600} sx={num}>{s.totalMarks} <Typography component="span" variant="body2" color="text.secondary">/ {r.maxMarks}</Typography></Typography>
                        : <Typography variant="body2" color="text.disabled">Not marked</Typography>}
                      {!markingReason && canManage && (
                        <Button size="small" onClick={() => setDialog({ kind: 'round', key: r.key })} sx={{ textTransform: 'none' }}>
                          {s ? 'Edit' : 'Enter'}
                        </Button>
                      )}
                    </Stack>
                    {s && <RoundAnswers scores={s.rubricScores} />}
                    {s?.comments && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{s.comments}</Typography>
                    )}
                  </Paper>
                );
              })}
            </Stack>
            {markingReason && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>{markingReason}</Typography>}
          </Box>
        )}

        {/* ── Additional criteria ────────────────────────────── */}
        {plan.custom && (
          <Box>
            <GroupLabel action={!lockReason && canManage && (
              <Button size="small" startIcon={<Edit fontSize="small" />} onClick={() => setDialog({ kind: 'custom' })} sx={{ textTransform: 'none' }}>
                Enter marks
              </Button>
            )}>
              Additional criteria
            </GroupLabel>
            <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
              {plan.custom.criteria.map((c, i) => {
                const v = app.customScores?.[c.key];
                return (
                  <Stack key={c.key} direction="row" alignItems="center" sx={{ px: 2, py: 1.25, borderTop: i ? 1 : 0, borderColor: 'divider' }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>{c.label}</Typography>
                    {v == null ? <Typography variant="body2" color="text.disabled">Not marked</Typography>
                      : <Typography variant="body2" fontWeight={600} sx={num}>{v} <Typography component="span" variant="body2" color="text.secondary">/ {c.maxMarks}</Typography></Typography>}
                  </Stack>
                );
              })}
            </Paper>
            {lockReason && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>{lockReason}</Typography>}
          </Box>
        )}
      </Stack>

      {dialog?.kind === 'test' && plan.test && (
        <TestScoresDialog
          open
          applicationId={app._id}
          papers={plan.test.papers}
          existingScores={app.testScores || []}
          passNote={`Pass mark ${plan.test.passingMarks} of ${plan.test.maxMarks}`}
          onCancel={() => setDialog(null)}
          onSaved={done}
        />
      )}
      {dialog?.kind === 'round' && plan.interview && (() => {
        const round = plan.interview.rounds.find(r => r.key === dialog.key);
        return round ? (
          <InterviewRoundDialog open applicationId={app._id} round={round} existing={scoredRound(round.key)} onCancel={() => setDialog(null)} onSaved={done} />
        ) : null;
      })()}
      {dialog?.kind === 'custom' && plan.custom && (
        <CustomScoresDialog
          open
          applicationId={app._id}
          criteria={plan.custom.criteria}
          existing={app.customScores || {}}
          onCancel={() => setDialog(null)}
          onSaved={done}
        />
      )}
    </>
  );
};

export default CandidateAssessmentTab;
